import React, { useMemo, useState } from 'react';
import { Divider, Link, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import ContentHeader from '../common/ContentHeader';
import Footer from './Footer';
import { mergeStyleSets } from '@fluentui/react';
import QRCodeComponent from '../common/QRCodeComponent';
import Login from './Login';
import Register from './Register';
import ForgetPwd from './ForgetPwd';
import RetrievePwdSuccess from './RetrievePwdSuccess';
import { useLocation, useNavigate } from 'react-router-dom';
import { initializeIcons } from '@fluentui/react/lib/Icons';

initializeIcons();

type IProps = {
  signInOut: (signInState: boolean) => void;
};

type IWelcomeStep = 'login' | 'register' | 'forgetPassword' | 'retrievePwdSuccess';
const Welcome: React.FC<IProps> = (props) => {
  const [welcomeStep, setWelcomeStep] = useState<IWelcomeStep>('login');

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [retrievePwdEmail, setRetrievePwdEmail] = useState<string | undefined>();

  const CurrentComponent = useMemo(() => {
    if (welcomeStep === 'login') {
      return (
        <Login
          onLoginSuccess={() => {
            props.signInOut(true);
            navigate(location.state?.from?.pathname ?? '/', { replace: true });
          }}
          onGoToRegister={() => setWelcomeStep('register')}
          onGoToForgetPwd={() => setWelcomeStep('forgetPassword')}
        />
      );
    }

    if (welcomeStep === 'register') {
      return (
        <Register
          onRegisterSuccess={() => {
            props.signInOut(true);
            navigate(location.state?.from?.pathname ?? '/', { replace: true });
          }}
          onBackToLogin={() => setWelcomeStep('login')}
        />
      );
    }

    if (welcomeStep === 'forgetPassword') {
      return (
        <ForgetPwd
          onBackToLogin={() => setWelcomeStep('login')}
          onSuccess={({ email }) => {
            setRetrievePwdEmail(email);
            setWelcomeStep('retrievePwdSuccess');
          }}
        />
      );
    }

    if (welcomeStep === 'retrievePwdSuccess') {
      return <RetrievePwdSuccess email={retrievePwdEmail} onBackToLogin={() => setWelcomeStep('login')} />;
    }
  }, [welcomeStep]);

  return (
    <div className={styles.container}>
      <ContentHeader title={t('welcome.slogan')} className={styles.header} />

      <div className={styles.body}>
        <div className={styles.aside}>
          <Text as='h3' block className={styles.subTitle}>
            {t('welcome.greetDownload')}
          </Text>
          <Text>
            {t('welcome.greetDownloadDescription1')}
            <Link
              href='https://fishmeet.top/install'
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: '#FF9900' }}>
              https://fishmeet.top/install
            </Link>
            {t('welcome.greetDownloadDescription2')}
          </Text>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              marginTop: 16
            }}>
            <QRCodeComponent url={'https://fishmeet.top/install'} width={150} height={150} options={{ margin: 0 }} />
          </div>
        </div>

        <Divider className={styles.divider} vertical style={{ height: '100%' }} />

        <div className={styles.main}>
          <div className={styles.mainContainer}>{CurrentComponent}</div>
        </div>
      </div>

      <Footer className={styles.footer} />
    </div>
  );
};

export default Welcome;

export const sharedStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  }
});

export const styles = mergeStyleSets({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 50px)'
  },

  header: {
    flexGrow: 0,
    flexShrink: 0
  },

  body: {
    display: 'flex',
    flexGrow: 1,
    padding: '40px 16px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  },

  divider: {
    flexGrow: '0 !important'
  },

  aside: [
    sharedStyles.container,
    {
      flex: '0 0 338px',
      padding: '0 16px'
    }
  ],

  main: [
    {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16
    }
  ],

  mainContainer: [
    {
      width: '384px',
      margin: '0 auto'
    }
  ],

  footer: {
    flex: '0'
  },

  subTitle: {
    textAlign: 'center !important',
    fontSize: '16px !important',
    fontWeight: 'bold !important'
  }
});
